import { api } from '@/services/api';
import { unifiedServices } from '@/services/unifiedService';
import { isSupabaseMode } from '@/config/dataSource';
import type { Tenant, CreateTenantData, UpdateTenantData } from '../types';
import { API_ROUTES } from '@/config/api';

export const tenantService = {
  // Listar todos os tenants
  async fetchTenants(): Promise<Tenant[]> {
    if (isSupabaseMode()) {
      const data = await unifiedServices.tenants.fetchTenants();
      return data.map(tenant => ({
        ...tenant,
        id: tenant.id.toString(),
        ativo: tenant.status === 'ATIVO',
        created_at: tenant.created_at,
        updated_at: tenant.updated_at,
        _count: {
          users: tenant._count?.users || 0,
          contratos: tenant._count?.contratos || 0
        }
      }));
    }
    const { data } = await api.get<Tenant[]>(API_ROUTES.SUPERADMIN.TENANTS.BASE);
    return data.map(tenant => ({
      ...tenant,
      id: tenant.id.toString(),
      ativo: tenant.status === 'ATIVO',
      created_at: new Date().toISOString(), // Temporário até o backend retornar essas datas
      updated_at: new Date().toISOString(),
      _count: {
        users: 0,
        contratos: 0
      }
    }));
  },

  // Buscar um tenant específico
  async fetchTenant(id: string): Promise<Tenant> {
    if (isSupabaseMode()) {
      const data = await unifiedServices.tenants.fetchTenant(id);
      return {
        ...data,
        id: data.id.toString(),
        ativo: data.status === 'ATIVO',
        created_at: data.created_at,
        updated_at: data.updated_at,
        _count: {
          users: data._count?.users || 0,
          contratos: data._count?.contratos || 0
        }
      };
    }
    const { data } = await api.get<Tenant>(API_ROUTES.SUPERADMIN.TENANTS.DETAILS(id));
    return {
      ...data,
      id: data.id.toString(),
      ativo: data.status === 'ATIVO',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _count: {
        users: 0,
        contratos: 0
      }
    };
  },

  // Criar um novo tenant
  async createTenant(tenantData: CreateTenantData): Promise<Tenant> {
    if (isSupabaseMode()) {
      const data = await unifiedServices.tenants.createTenant(tenantData);
      return {
        ...data,
        id: data.id.toString(),
        ativo: data.status === 'ATIVO',
        created_at: data.created_at,
        updated_at: data.updated_at,
        _count: {
          users: 0,
          contratos: 0
        }
      };
    }
    // Remove a propriedade status pois o backend não aceita no DTO de criação
    const { ativo, ...tenantDataWithoutStatus } = tenantData;
    
    const { data } = await api.post<Tenant>(API_ROUTES.SUPERADMIN.TENANTS.BASE, tenantDataWithoutStatus);
    return {
      ...data,
      id: data.id.toString(),
      ativo: data.status === 'ATIVO',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _count: {
        users: 0,
        contratos: 0
      }
    };
  },

  // Atualizar um tenant existente
  async updateTenant(tenantData: UpdateTenantData): Promise<Tenant> {
    if (isSupabaseMode()) {
      const data = await unifiedServices.tenants.updateTenant(tenantData);
      return {
        ...data,
        id: data.id.toString(),
        ativo: data.status === 'ATIVO',
        created_at: data.created_at,
        updated_at: data.updated_at,
        _count: {
          users: 0,
          contratos: 0
        }
      };
    }
    const { data } = await api.put<Tenant>(API_ROUTES.SUPERADMIN.TENANTS.DETAILS(tenantData.id), {
      ...tenantData,
      status: tenantData.ativo ? 'ATIVO' : 'INATIVO'
    });
    return {
      ...data,
      id: data.id.toString(),
      ativo: data.status === 'ATIVO',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _count: {
        users: 0,
        contratos: 0
      }
    };
  },

  // Excluir um tenant
  async deleteTenant(id: string): Promise<void> {
    if (isSupabaseMode()) {
      return await unifiedServices.tenants.deleteTenant(id);
    }
    await api.delete(API_ROUTES.SUPERADMIN.TENANTS.DETAILS(id));
  },

  // Ativar/desativar um tenant
  async toggleTenantStatus(id: string, active: boolean): Promise<Tenant> {
    if (isSupabaseMode()) {
      const data = await unifiedServices.tenants.toggleTenantStatus(id, active);
      return {
        ...data,
        id: data.id.toString(),
        ativo: data.status === 'ATIVO',
        created_at: data.created_at,
        updated_at: data.updated_at,
        _count: {
          users: 0,
          contratos: 0
        }
      };
    }
    const { data } = await api.put<Tenant>(API_ROUTES.SUPERADMIN.TENANTS.DETAILS(id), {
      status: active ? 'ATIVO' : 'INATIVO'
    });
    return {
      ...data,
      id: data.id.toString(),
      ativo: data.status === 'ATIVO',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _count: {
        users: 0,
        contratos: 0
      }
    };
  }
}; 